// @ts-check

import React, { useState } from "react";
import { Alert, Card } from "antd";
import {
  ArrowRightOutlined,
  CheckSquareTwoTone,
  CloseSquareTwoTone,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";

import { colors } from "@evolved/constants";

/**
 * @param {Object} props 
 * @param {number} props.index 0 based index of import row
 * @param {import("./domain").ImportRowInfo[]} props.info
 * @param {((info: import("./domain").ImportRowInfo) => boolean)} [props.filter]
 */
export const RowInfo = ({ index, info, filter }) => {
  const [isRootOpen, setIsRootOpen] = useState(true);

  /** @type {string[]} */
  const defaultIsOpen = [];
  const [isOpen, setIsOpen] = useState(defaultIsOpen);

  /**
   * @param {string} index
   */
  const onToggleDetails = (index) => {
    if (isOpen.includes(index)) {
      setIsOpen(isOpen.filter(_index => _index !== index));
    } else {
      setIsOpen([...isOpen, index]);
    }
  }

  /** @type {import("./domain").ImportRowInfo[]} */
  const errors = [];
  /** @type {import("./domain").ImportRowInfo[]} */
  const warnings = [];

  for (const i of info) {
    if (filter && !filter(i)) {
      continue;
    }

    if (i.level === "error") {
      errors.push(i);
    }

    if (i.level === "warning") {
      warnings.push(i);
    }
  }

  if (!errors.length && !warnings.length) {
    return null;
  }

  return (
    <Card size="small">
      <div className="font-bold flex cursor-pointer" onClick={() => setIsRootOpen(!isRootOpen)}>
        <div className="mr-4">
          {isRootOpen ? <DownOutlined /> : <RightOutlined />}
        </div>
        <div>
          Row {index + 1}
        </div>

        <div className="ml-auto flex space-x-1 mr-1">
          <div>
            {errors.length ? <div>{errors.length} Error(s)</div> : null}
          </div>
          <div>
            {warnings.length ? <div>{warnings.length} Warning(s)</div> : null}
          </div>
        </div>
      </div>
      {isRootOpen ? errors.map((i, index) => {
        if (!isOpen || i.level !== "error") {
          return null;
        }

        /** @type {React.JSX.Element | null} */
        let message = null;

        if (i.type === "entity_match_info") {
          // TODO: maybe some more info about why this doesn't match
          // - maybe some info about how to get a better match

          message = (
            <>
              <div className="flex">
                <div className="mr-4">
                  {isOpen.includes(`error_${index}`) ? <DownOutlined /> : <RightOutlined />}
                </div>
                <div className="font-semibold" >
                  No definite match, skipping row.
                </div>
              </div>
              {isOpen.includes(`error_${index}`) ? (
                <>
                  <div className="space-y-2 mb-2">
                    {i.matches.map((match, index) => {
                      // NOTE: assuming a match on each index

                      return (
                        <div key={index}>
                          <div className="mb-2">{match.field} ={" "}
                            <strong>{match.key}</strong> matched multiple:
                          </div>
                          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                          </div>
                          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                            <div>
                              <div>Rows</div>
                              {match.rowIndexes.map((index) => index + 1).join(", ")}
                            </div>
                            <ArrowRightOutlined />
                            <div>
                              <div>Entities</div>
                              {match.entityIds.join(", ")}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div >
                    Match key must match one row to one entity. Use CRM ID from export to ensure this.
                  </div>
                </>
              ) : null}
            </>
          );
        } else if (i.type === "entity_validation_failed") {
          message = (
            <div>
              <div className="text-lg">Entity validation Error</div>
              {i.errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )
        }

        if (!message) {
          return null;
        }

        return (
          <Alert
            message={message}
            onClick={() => onToggleDetails(`error_${index}`)}
            rootClassName="cursor-pointer"
            type="error"
          />
        );
      }) : null}

      {isRootOpen ? warnings.map((i, index) => {
        if (i.level !== "warning") {
          return null;
        }

        /** @type {React.JSX.Element | null} */
        let message = null;

        if (i.type === "relationship_match_info") {
          // TODO: if cardinality overflow, don't try to match any

          if (i.warnings.includes("cardinality_overflow")) {
            message = (
              <div>Field only supports one entity, cannot relate multiple.</div>
            );
          } else {
            message = (
              <>
                {i.warnings.includes("overwrite_existing_with_nothing") ? (
                  <div className="flex">
                    <div className="mr-4">
                      {isOpen.includes(`warning_${index}`) ? <DownOutlined /> : <RightOutlined />}
                    </div>
                    <div className="font-semibold" >
                      No matches, so leaving current in place.
                    </div>
                  </div>
                ) : (
                  <div className="flex">
                    <div className="mr-4">
                      {isOpen.includes(`warning_${index}`) ? <DownOutlined /> : <RightOutlined />}
                    </div>
                    <div className="font-semibold" >
                      No definite match for some values.
                    </div>
                  </div>
                )}
                {isOpen.includes(`warning_${index}`) ? (
                  i.splitValues.map(({ matches, isValid, value }, index) => {
                    return (
                      <div key={index} className="mt-2">
                        <div className="mb-2 flex items-center">
                          <div className="mr-2"><strong>{value}</strong></div>
                          {isValid ?
                            <CheckSquareTwoTone twoToneColor={colors.SUCCESS} /> :
                            <CloseSquareTwoTone twoToneColor={colors.URGENT} />
                          }
                        </div>
                        <div style={{ display: "flex" }}>
                          <div style={{ flexGrow: 1 }} className="space-y-2">
                            {matches.map((match, _index) => {
                              return (
                                <div
                                  key={`${index}_${_index}`}
                                  style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}
                                >
                                  <div className="flex-1">
                                    {match.field}
                                  </div>
                                  <ArrowRightOutlined className="mr-4 mt-1" />
                                  {match.entityIds.length ? (

                                    <div className="flex-1">
                                      <div>{match.entityIds.length > 1 ? "Multiple " : ""}Matches:</div>
                                      {match.entityIds.join(", ")}
                                    </div>
                                  ) : (
                                    <div className="flex-1">
                                      No matches</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : null}
              </>
            )

          }

        } else if (i.type === "value_conversion_error") {
          message = (
            <div>
              <div>Could not convert value at column {i.headerIndex + 1}: {i.error}</div>
            </div>
          );
        }

        if (!message) {
          return null;
        }

        return (
          <div className="mt-2">
            <Alert
              message={message}
              onClick={() => onToggleDetails(`warning_${index}`)}
              rootClassName="cursor-pointer"
              type="warning"
            />
          </div>
        );
      }) : null}
    </Card >
  )

};
